/**
 * Server functions and utilities for the Portals app.
 *
 * tRPC-backed queries and mutations are now accessed via the typed `trpc`
 * proxy from `@/lib/trpc`:
 *
 *   import { trpc } from '@/lib/trpc';
 *   trpc.portal.getAuthor.queryOptions({ portalId, locale })
 *   trpc.payments.preregisterDonation.mutationOptions()
 *
 * This file retains:
 * - Direct server functions (not suitable for tRPC: streaming, etc.)
 * - Utility functions (ensureSeoData, extractErrorInfo, currentWindowKey)
 */

// ── Imports ──────────────────────────────────────────────────────────────────

import { createServerFn } from '@tanstack/react-start';
import { getRequestHeader } from '@tanstack/react-start/server';
import { isServer } from '@tanstack/react-query';
import { parseRequest, parseIp } from './server-functions-shared.server';
import { eventEmitter } from './server-broadcast.server';
import * as z from 'zod';
import { portalLocalesExtended } from '@worldcoo/monolith/portals';

// ── Types ────────────────────────────────────────────────────────────────────

export type PutProjectErrorCode = 'PROJECT_TOO_LARGE' | 'SAVE_PROJECT_FAILED';
export type TranslateErrorCode = 'UNAUTHORIZED' | 'CAPTCHA_FAILED' | 'TRANSLATION_FAILED';
export type ContractErrorCode = 'NETWORK_ERROR' | 'CAPTCHA_FAILED' | 'CONTRACT_GENERATION_FAILED';
export type DraftContractErrorCode = 'DRAFT_NOT_FOUND' | 'CONTRACT_GENERATION_FAILED';
export type OnboardingDraftErrorCode = 'NETWORK_ERROR' | 'SAVE_FAILED';
export type LiveCounterMode = 'day' | 'realtime';

// ── Shared helpers ────────────────────────────────────────────────────────────

export async function ensureSeoData(context: { isBot: boolean }, promise: Promise<unknown>): Promise<void> {
	if (isServer && context.isBot) {
		await promise;
	}
}

const WINDOW_MS = 5 * 60_000;

/** Returns the current 5-minute window key (e.g. "2026-02-13T14:10") */
export function currentWindowKey() {
	const windowStart = Math.floor(Date.now() / WINDOW_MS) * WINDOW_MS;
	return new Date(windowStart).toISOString().slice(0, 16);
}

// ── Server functions that are called directly (not through tRPC) ─────────────

export const getRequestContext = createServerFn()
	.validator(z.enum(portalLocalesExtended).optional())
	.handler(({ data }) => parseRequest(data));

export const logErrorFn = createServerFn({ method: 'POST' })
	.validator(
		z.object({
			route: z.string().catch('unknown'),
			name: z.string().catch('UnknownError'),
			message: z.string().catch('(no message)'),
			stack: z.string().optional(),
		}),
	)
	.handler(({ data }) => {
		const request = parseRequest();
		const requestIp = parseIp();
		// Exactly ONE line may contain the literal `PORTAL_FRONTEND_ERROR` — the monolith log-subscription
		// parser tickets every occurrence of that marker (case-sensitive `/PORTAL_FRONTEND_ERROR/`, see
		// apps/monolith/system-errors/parse-log-subscription-event.ts). The structured context below used to
		// carry a `type: 'PORTAL_FRONTEND_ERROR'` field, which the parser matched as a SECOND, malformed
		// ticket (`type: "PORTAL_FRONTEND_ERROR",`). Keep the marker on the `console.error` line only.
		console.error(`PORTAL_FRONTEND_ERROR: ${data.name} — ${data.message}`);
		console.info({
			event: 'portal-frontend-error',
			timestamp: new Date().toISOString(),
			portalId: request.portalId,
			locale: request.locale,
			baseUrl: request.baseUrl,
			ip: requestIp,
			userAgent: getRequestHeader('User-Agent') ?? undefined,
			...data,
		});
		return { success: true, logged: true };
	});

// ── Streaming server functions ───────────────────────────────────────────────

export const waitForConfirmedDonationFn = createServerFn()
	.validator(z.string())
	.handler(async function* ({ data: donationIdToConfirm }) {
		const promise = new Promise<'donation.confirmed' | 'donation.timeout'>((resolve) => {
			const onConfirmed = (donationId: string) => {
				if (donationId === donationIdToConfirm) {
					eventEmitter.off('donation.confirmed', onConfirmed);
					resolve('donation.confirmed');
				}
			};
			eventEmitter.on('donation.confirmed', onConfirmed);
			setTimeout(() => {
				eventEmitter.off('donation.confirmed', onConfirmed);
				resolve('donation.timeout');
			}, 10_000);
		});
		yield await promise;
	});

// ── Utility functions ────────────────────────────────────────────────────────

export function extractErrorInfo(error: unknown): {
	name: string;
	message: string;
	stack: string | undefined;
} {
	if (error instanceof Error) {
		return { name: error.name || 'Error', message: error.message || '(empty message)', stack: error.stack };
	}
	if (typeof error === 'string') {
		return { name: 'StringError', message: error, stack: undefined };
	}
	if (error === null || error === undefined) {
		return { name: 'NullishError', message: `Thrown value was ${String(error)}`, stack: undefined };
	}
	if (typeof error === 'object') {
		const name = 'name' in error && typeof error.name === 'string' ? error.name : 'ObjectError';
		const message =
			'message' in error && typeof error.message === 'string'
				? error.message
				: 'error' in error && typeof error.error === 'string'
					? error.error
					: safeStringify(error);
		const stack = 'stack' in error && typeof error.stack === 'string' ? error.stack : undefined;
		return { name, message, stack };
	}
	return { name: 'UnknownError', message: safeStringify(error), stack: undefined };
}

function safeStringify(value: unknown): string {
	try {
		return JSON.stringify(value);
	} catch {
		return String(value);
	}
}

// Moved to its own dependency-free module so it can be unit-tested without pulling in the
// server-function imports above; re-exported here to keep existing import sites working.
export { shouldReportFrontendError } from './frontend-error-filter';
